import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react'

export interface SignaturePadHandle {
  /** Returns a PNG data URL, or null when nothing has been drawn. */
  toDataURL(): string | null
  clear(): void
  isEmpty(): boolean
}

interface Props {
  /** Drawing area height in CSS pixels. Width always fills the container. */
  height?: number
  onChange?: (hasInk: boolean) => void
}

/** A sample, in the pad's own unscaled CSS pixels, from its top left corner. */
export interface Point {
  x: number
  y: number
}

/**
 * The pointer that is drawing: which one it is, the piece of stroke it is
 * adding to, where it was last (in the pixels the pad is shown at) and
 * whether that was off the pad.
 */
export interface Pointer {
  id: number
  piece: Point[] | null
  last: Point
  outside: boolean
}

/** The pen every stroke is drawn with, live, when it is drawn again, and when it is exported. */
function setPen(ctx: CanvasRenderingContext2D) {
  ctx.lineWidth = 2.4
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.strokeStyle = '#101828'
}

/** One curve: from the midpoint of a and b, through b, to the midpoint of b and c. */
function drawSegment(ctx: CanvasRenderingContext2D, a: Point, b: Point, c: Point) {
  // Curve through the midpoint of the last two samples: the midpoint is
  // guaranteed to lie on the curve, so successive segments join smoothly.
  const midAB = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
  const midBC = { x: (b.x + c.x) / 2, y: (b.y + c.y) / 2 }
  ctx.beginPath()
  ctx.moveTo(midAB.x, midAB.y)
  ctx.quadraticCurveTo(b.x, b.y, midBC.x, midBC.y)
  ctx.stroke()
}

/**
 * Draws every stroke again exactly as it was drawn the first time: the same
 * curves, in the same order, with the same pen. Redrawing the whole stroke as
 * one path would join it differently and leave the second picture unlike the
 * first.
 */
function replay(ctx: CanvasRenderingContext2D, strokes: Point[][]) {
  for (const stroke of strokes) {
    for (let i = 2; i < stroke.length; i++) drawSegment(ctx, stroke[i - 2], stroke[i - 1], stroke[i])
  }
}

/** A piece of stroke draws ink once it has three samples; a tap draws nothing. */
function drawsInk(piece: Point[]): boolean {
  return piece.length >= 3
}

/** The box around every sample that drew ink, or null when nothing has. */
function inkBounds(strokes: Point[][]) {
  let b: { minX: number; minY: number; maxX: number; maxY: number } | null = null
  for (const piece of strokes) {
    if (!drawsInk(piece)) continue
    for (const p of piece) {
      if (!b) b = { minX: p.x, minY: p.y, maxX: p.x, maxY: p.y }
      else {
        b.minX = Math.min(b.minX, p.x)
        b.minY = Math.min(b.minY, p.y)
        b.maxX = Math.max(b.maxX, p.x)
        b.maxY = Math.max(b.maxY, p.y)
      }
    }
  }
  return b
}

/**
 * The scale the pad shows its strokes at: 1 while the drawing fits, less once
 * the pad has become narrower than the drawing, so all of it stays in view at
 * one scale. Only the picture is scaled. The strokes are kept as drawn, and
 * the image sent for sealing is drawn from them at full size.
 *
 * A scaled drawing ends 8px inside the edge, exportInk's margin. Scaled to end
 * exactly at the edge, the last sample sat on it and half the pen's width
 * past it was cut off.
 */
function fitScale(strokes: Point[][], width: number, height: number): number {
  const b = inkBounds(strokes)
  if (!b || width <= 0 || height <= 0) return 1
  const margin = 8
  let s = 1
  if (b.maxX > width) s = Math.min(s, Math.max(width - margin, 1) / b.maxX)
  if (b.maxY > height) s = Math.min(s, Math.max(height - margin, 1) / b.maxY)
  return s
}

/**
 * Sizes the canvas to the pad and draws every stroke on it again, at the
 * scale fitScale gives. Answers that scale, which the next samples are
 * divided by.
 *
 * Setting a canvas's size wipes it, so this runs on every change of width:
 * a tablet turned, or a window resized across the layout's breakpoint. It used
 * to wipe the pixels and keep the claim that the pad held a signature, and
 * "Sign document" then sent a transparent image to be sealed.
 */
function paint(
  canvas: HTMLCanvasElement,
  strokes: Point[][],
  width: number,
  height: number,
  dpr: number,
): number {
  canvas.width = Math.floor(width * dpr)
  canvas.height = Math.floor(height * dpr)
  canvas.style.height = `${height}px`
  const scale = fitScale(strokes, width, height)
  const ctx = canvas.getContext('2d')
  if (!ctx) return scale
  ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0)
  setPen(ctx)
  replay(ctx, strokes)
  return scale
}

/** Where the line from a point on the pad to a point off it crosses the pad's edge. */
function edgePoint(on: Point, off: Point, width: number, height: number): Point {
  const dx = off.x - on.x
  const dy = off.y - on.y
  let t = 1
  if (off.x < 0 && dx !== 0) t = Math.min(t, -on.x / dx)
  else if (off.x > width && dx !== 0) t = Math.min(t, (width - on.x) / dx)
  if (off.y < 0 && dy !== 0) t = Math.min(t, -on.y / dy)
  else if (off.y > height && dy !== 0) t = Math.min(t, (height - on.y) / dy)
  return {
    x: Math.min(width, Math.max(0, on.x + dx * t)),
    y: Math.min(height, Math.max(0, on.y + dy * t)),
  }
}

/**
 * Adds one sample from the drawing pointer to the strokes, and answers the
 * piece of stroke that grew, or null when none did.
 *
 * A sample off the pad is not ink. The browser keeps reporting a pointer it
 * has captured after it leaves the canvas, and the canvas hides what is drawn
 * there, so the piece ends at the edge it crossed and a new one starts where
 * the pointer comes back. What is kept is then what was shown, and nothing
 * the signer never saw can reach the sealed image.
 *
 * `p` is in the pixels the pad is shown at; what is kept is divided by
 * `scale`, so every stroke stays at one scale however the pad is shown.
 */
function follow(
  pointer: Pointer,
  strokes: Point[][],
  p: Point,
  width: number,
  height: number,
  scale: number,
): Point[] | null {
  const kept = (q: Point) => ({ x: q.x / scale, y: q.y / scale })
  const inside = p.x >= 0 && p.y >= 0 && p.x <= width && p.y <= height
  let grew: Point[] | null = null
  if (inside && !pointer.outside && pointer.piece) {
    pointer.piece.push(kept(p))
    grew = pointer.piece
  } else if (inside) {
    // Back onto the pad: a new piece, from the edge it crossed.
    pointer.piece = [kept(edgePoint(p, pointer.last, width, height)), kept(p)]
    strokes.push(pointer.piece)
    grew = pointer.piece
  } else if (!pointer.outside && pointer.piece) {
    // Off the pad: the piece ends at the edge.
    pointer.piece.push(kept(edgePoint(pointer.last, p, width, height)))
    grew = pointer.piece
    pointer.piece = null
  }
  pointer.outside = !inside
  pointer.last = p
  return grew
}

/**
 * The image sent for sealing, drawn from the strokes rather than copied off
 * the canvas: transparent, trimmed to the ink with a small margin so the
 * signature fills its box in the PDF instead of floating inside a mostly empty
 * rectangle, and at full size whatever scale the pad is shown at. Null when
 * nothing has been drawn.
 */
function exportInk(
  strokes: Point[][],
  dpr: number,
  makeCanvas: () => HTMLCanvasElement,
): string | null {
  const b = inkBounds(strokes)
  if (!b) return null
  const pad = 8
  const out = makeCanvas()
  out.width = Math.ceil((b.maxX - b.minX + pad * 2) * dpr)
  out.height = Math.ceil((b.maxY - b.minY + pad * 2) * dpr)
  const ctx = out.getContext('2d')
  if (!ctx) return null
  ctx.setTransform(dpr, 0, 0, dpr, (pad - b.minX) * dpr, (pad - b.minY) * dpr)
  setPen(ctx)
  replay(ctx, strokes)
  return out.toDataURL('image/png')
}

/**
 * The pad's model, for its tests (web/src/signaturePad.test.ts), which run
 * without a browser. One object rather than four exports, so the component
 * file stays as close as it can to exporting only components.
 *
 * It stays in this file, beside the drawing it models, because the app's
 * test (mobile/src/signature/__tests__/smoothing.test.ts) reads the pen, the
 * curve and the margin from this file by name, so the two pads cannot drift.
 * The lint rule is about fast refresh in development: an edit to this file
 * reloads the page instead of swapping the component, which costs nothing a
 * signer sees. Moving the model to a module of its own waits for that test to
 * read it there (a request in build-notes/Z-web.md).
 */
// oxlint-disable-next-line react/only-export-components
export const padModel = { fitScale, paint, follow, exportInk }

/**
 * SignaturePad captures a drawn signature.
 *
 * Strokes are smoothed by drawing quadratic curves through the midpoints of
 * successive samples, which turns the browser's coarse pointer events into a
 * line that looks handwritten rather than polygonal.
 *
 * The signature is the list of strokes, not the canvas. The canvas is a
 * picture of them, drawn again whenever the pad changes size, and the PNG sent
 * for sealing is drawn from them too, so what the pad shows, whether it says
 * it holds a signature, and what is sealed can never disagree.
 */
export const SignaturePad = forwardRef<SignaturePadHandle, Props>(function SignaturePad(
  { height = 200, onChange },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)
  const strokes = useRef<Point[][]>([])
  const pointer = useRef<Pointer | null>(null)
  // The scale the strokes are shown at, from the last paint.
  const scale = useRef(1)
  const [hasInk, setHasInk] = useState(false)

  // The pad fills whatever width it is given, and the strokes are drawn again
  // at every width. They are never stretched: a pad narrower than the drawing
  // shows all of it smaller, and a wider one shows it as it was drawn.
  useEffect(() => {
    const canvas = canvasRef.current
    const wrap = wrapRef.current
    if (!canvas || !wrap) return

    function resize() {
      const width = wrap!.clientWidth
      if (width <= 0) return
      scale.current = paint(canvas!, strokes.current, width, height, window.devicePixelRatio || 1)
    }

    resize()
    const observer = new ResizeObserver(resize)
    observer.observe(wrap)
    return () => observer.disconnect()
  }, [height])

  // iOS Safari treats touch listeners as passive unless we say otherwise, so
  // the page scrolls under the finger while the signer is drawing. touch-action
  // on the canvas is not enough on every Safari the ceremony has to survive.
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const stop = (e: TouchEvent) => { e.preventDefault() }
    canvas.addEventListener('touchstart', stop, { passive: false })
    canvas.addEventListener('touchmove', stop, { passive: false })
    return () => {
      canvas.removeEventListener('touchstart', stop)
      canvas.removeEventListener('touchmove', stop)
    }
  }, [])

  function start(e: React.PointerEvent<HTMLCanvasElement>) {
    // One pointer draws at a time. A second finger landing mid-stroke used to
    // restart the stroke from wherever it touched.
    if (pointer.current) return
    e.currentTarget.setPointerCapture(e.pointerId)
    const rect = e.currentTarget.getBoundingClientRect()
    const p = { x: e.clientX - rect.left, y: e.clientY - rect.top }
    const piece = [{ x: p.x / scale.current, y: p.y / scale.current }]
    strokes.current.push(piece)
    pointer.current = { id: e.pointerId, piece, last: p, outside: false }
  }

  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    const drawing = pointer.current
    if (!drawing || drawing.id !== e.pointerId) return
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return

    const rect = e.currentTarget.getBoundingClientRect()
    const pts = follow(drawing, strokes.current, { x: e.clientX - rect.left, y: e.clientY - rect.top },
      rect.width, rect.height, scale.current)
    if (!pts) return
    if (pts.length < 3) return

    drawSegment(ctx, pts[pts.length - 3], pts[pts.length - 2], pts[pts.length - 1])

    if (!hasInk) {
      setHasInk(true)
      onChange?.(true)
    }
  }

  function end(e: React.PointerEvent<HTMLCanvasElement>) {
    if (pointer.current?.id !== e.pointerId) return
    pointer.current = null
    // A tap draws nothing, so there is nothing of it to keep.
    strokes.current = strokes.current.filter(drawsInk)
  }

  useImperativeHandle(ref, () => ({
    isEmpty: () => !hasInk,
    clear() {
      strokes.current = []
      pointer.current = null
      const canvas = canvasRef.current
      const width = wrapRef.current?.clientWidth ?? 0
      if (canvas && width > 0) {
        scale.current = paint(canvas, strokes.current, width, height, window.devicePixelRatio || 1)
      }
      setHasInk(false)
      onChange?.(false)
    },
    toDataURL() {
      if (!hasInk) return null
      return exportInk(strokes.current, window.devicePixelRatio || 1, () => document.createElement('canvas'))
    },
  }))

  return (
    <div className="sig-pad-wrap" ref={wrapRef}>
      <canvas
        ref={canvasRef}
        className="sig-pad"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        onPointerCancel={end}
      />
      {/* A guide line drawn over the canvas rather than into it, so it never
          becomes part of the exported signature image. */}
      <div className="sig-guide" aria-hidden>
        <span className="sig-guide-line" />
        {!hasInk && <span className="sig-guide-hint">Sign here</span>}
      </div>
    </div>
  )
})
